import React, { FC } from 'react';
import OrganizationModal from 'shared/components/OrganizationModal';
import styles from './isSwitchToMonthlyModal.module.scss';
import InputField from 'shared/components/InputField';
import COSelect from 'shared/components/COSelect';
import { Form, Formik } from 'formik';
import {
  monthList,
  monthListWithFullName,
} from 'views/CompanySetup/companySetupConstants';
import COButton from 'shared/components/COButton';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import useFilters from 'shared/hooks/useFilters';
import { queryParamsList } from '..';
import { Col, Row } from 'antd';
import { naturalGasUnitOptions } from '../ScopeOneViewComponent';
import {
  ScopeData,
  ScopeMonthlyData,
} from 'models/ActivityDataEntryScopes/scopes.model';
import { getMonthName, getMonthNameWithNumber } from 'shared/utils/monthName';
import { Scope1FieldsEnums } from 'enums/ScopeOneFieldEnums';
import { monthlyValidationSchema } from './switchToMonthlyValidationSchema';
import { convertToReadableText } from 'shared/utils/convertToUserReadable';
import { allowOnlyNumbersUptoN } from 'shared/utils/convertFormData';
interface SwitchToMonthlyModalProps {
  isModalOpen: boolean;
  handleSubmit: (values?: ScopeData) => void;
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  initialValues?: ScopeData;
}

const SwitchToMonthlyModal: FC<SwitchToMonthlyModalProps> = ({
  isModalOpen,
  setIsModalOpen,
  initialValues = new ScopeData(),
  handleSubmit,
}) => {
  const handleClose = () => setIsModalOpen?.(false);
  const { organizationData } = useParentOrganizationContext();
  const { getParams } = useFilters();
  const { isEditView, activeTab } = getParams(queryParamsList);
  const desiredIntialValues: ScopeData = {
    ...initialValues,
    scopeMonthlyData: initialValues?.scopeMonthlyData
      ? initialValues?.scopeMonthlyData
      : monthListWithFullName?.map(month => {
          return {
            month: Number(month?.value),
            value: undefined,
          };
        }),
  };
  const { startMonth, endMonth } = getMonthName(
    organizationData?.company?.reportingMonth ?? 1
  );
  const rearrangedMonthList = (monthListWithFullName?: ScopeMonthlyData[]) => {
    if (!monthListWithFullName) return;
    const firstPart = monthListWithFullName.slice(
      organizationData?.company?.reportingMonth
        ? organizationData?.company?.reportingMonth - 1
        : 1
    );
    const secondPart = monthListWithFullName.slice(
      0,
      organizationData?.company?.reportingMonth
        ? organizationData?.company?.reportingMonth - 1
        : 1
    );
    return [...firstPart, ...secondPart];
  };
  const sortedMonthList = rearrangedMonthList(
    monthListWithFullName as ScopeMonthlyData[]
  );
  const sortedData = initialValues?.scopeMonthlyData?.sort(
    (a, b) => (a?.month ?? 0) - (b?.month ?? 0)
  );
  const sortedResponse = rearrangedMonthList(sortedData);

  return (
    <div className={styles.monthlyModalContainer}>
      <OrganizationModal
        visible={isModalOpen}
        onClose={handleClose}
        wrapClassName={styles.modalContent}
        width={isEditView ? 700 : 400}
        children={
          <Formik
            initialValues={desiredIntialValues}
            enableReinitialize
            validationSchema={monthlyValidationSchema}
            validateOnMount
            onSubmit={handleSubmit}>
            {({ values, isValid }) => (
              <Form>
                {isEditView ? (
                  <div className={styles.monthlyContainer}>
                    <div className={styles.headerContainer}>
                      <div>
                        <div className={styles.headerText}>
                          {values?.getPlaceholder?.()
                            ? values?.getPlaceholder?.()
                            : convertToReadableText(values?.fuelName)}
                        </div>
                        <div className={styles.subHeading}>
                          Enter monthly data for {startMonth} {activeTab} -
                          {' ' + endMonth + ' '}
                          {startMonth === monthList?.[0]?.label
                            ? activeTab
                            : activeTab + 1}
                        </div>
                      </div>
                      {values?.fuelName === Scope1FieldsEnums.NATURAL_GAS && (
                        <div className={styles.unitContainer}>
                          <COSelect
                            name="unit"
                            placeholderstring="Units"
                            label="Units"
                            options={naturalGasUnitOptions}
                            allowClear={false}
                          />
                        </div>
                      )}
                    </div>
                    <div className={styles.monthlyFieldContainer}>
                      {sortedMonthList?.map(month => (
                        <div
                          className={styles.fieldContainer}
                          key={month?.label}>
                          <InputField
                            name={`scopeMonthlyData.${
                              Number(month.value) - 1
                            }.value`}
                            placeholderstring={month?.label ?? ''}
                            label={month?.label}
                            onKeyDown={allowOnlyNumbersUptoN()}
                            isNullable={true}
                          />
                        </div>
                      ))}
                    </div>
                    <div className={styles.buttonContainer}>
                      <COButton
                        primary
                        children="Submit"
                        submit
                        disabled={!isValid}
                      />
                      <COButton
                        secondary
                        children="Back"
                        onClick={handleClose}
                      />
                    </div>
                  </div>
                ) : (
                  <div className={styles.monthlyViewContainer}>
                    <div className={styles.sourceTitle}>
                      {values?.getPlaceholder?.()
                        ? values?.getPlaceholder?.()
                        : ''}
                    </div>
                    {sortedResponse?.map(month => (
                      <Row
                        className={styles.monthlyDataContainer}
                        key={month?.month}>
                        <Col span={12} className={styles.monthName}>
                          {getMonthNameWithNumber(Number(month?.month))}
                        </Col>
                        <Col span={12} className={styles.monthValue}>
                          {month?.value
                            ? values?.unit
                              ? month?.value + ' ' + values?.unit
                              : month?.value
                            : '-'}
                        </Col>
                      </Row>
                    ))}
                  </div>
                )}
              </Form>
            )}
          </Formik>
        }
      />
    </div>
  );
};

export default SwitchToMonthlyModal;
